import type { PrizeDraw, WonPrize } from "@/domain/marketing/api"
import { getMerchantId } from "@/lib/company-storage"
import { keepCouponForLater } from "@/lib/pending-coupon"
import { useStore } from "jotai"
import { useEffect, useState } from "react"
import { reloadWonPrizes } from "./use-won-prizes"

const POLL_MS = 1500
const MAX_POLLS = 10

/**
 * A won draw's coupon as the sheet shows it: `issuing` while coupon-service
 * creates the code (the first draw answer never has it), `ready` with the code
 * and the prize's validity, `late` when it did not arrive while the sheet was
 * open — it still lands in "Meus prêmios".
 */
export type IssuedPrize =
  | { status: "issuing" }
  | { status: "ready"; code: string; prize: WonPrize | null }
  | { status: "late" }

/**
 * Follows a won draw until its code is issued, reading the customer's prizes
 * every 1.5 s for up to 15 s, and keeps the code for later once it is known.
 * Null for a miss.
 */
export function useIssuedPrize(draw: PrizeDraw | null): IssuedPrize | null {
  const store = useStore()
  const [result, setResult] = useState<{ drawId: string; issued: IssuedPrize } | null>(null)
  const drawId = draw?.won ? draw.drawId : null
  const drawnCode = draw?.won ? (draw.code ?? "") : ""

  useEffect(() => {
    if (!drawId) return
    let cancelled = false
    let timer: number | undefined
    let polls = 0

    function settle(issued: IssuedPrize) {
      if (cancelled || !drawId) return
      if (issued.status === "ready") {
        const merchantId = getMerchantId()
        if (merchantId) keepCouponForLater(issued.code, merchantId)
      }
      setResult({ drawId, issued })
    }

    function poll() {
      polls += 1
      void reloadWonPrizes(store).then((prizes) => {
        if (cancelled) return
        const prize = prizes?.find((entry) => entry.drawId === drawId) ?? null
        const code = drawnCode || prize?.code || ""
        if (code) return settle({ status: "ready", code, prize })
        if (polls >= MAX_POLLS) return settle({ status: "late" })
        timer = window.setTimeout(poll, POLL_MS)
      })
    }

    if (drawnCode) settle({ status: "ready", code: drawnCode, prize: null })
    poll()
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [drawId, drawnCode, store])

  if (!drawId) return null
  if (result?.drawId === drawId) return result.issued
  return drawnCode ? { status: "ready", code: drawnCode, prize: null } : { status: "issuing" }
}
